import { IbcShipTypeEnum } from 'src/enums/stowage/ibcShipTypeEnum';
import { MarpolPollutionCategoryEnum } from 'src/enums/stowage/marpolPollutionCategoryEnum';
import { IbcTankEnvironmentalControlEnum } from 'src/enums/stowage/ibcTankEnvironmentalControlEnum';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { OperationType } from 'src/constants/stowage/operationTypes';

export enum OptionValueType {
    NA,
    CpWeight,
    CpWeightMinusOption,
    CpWeightPlusOption,
}
export interface OptionConfiguration {
    optionType: string;
    nominatedValueType: OptionValueType;
    minValueType: OptionValueType;
    maxValueType: OptionValueType;
}

export const optionsConfiguration: OptionConfiguration[] = [
    {
        optionType: 'MOLOO',
        nominatedValueType: OptionValueType.CpWeightPlusOption,
        minValueType: OptionValueType.CpWeightMinusOption,
        maxValueType: OptionValueType.CpWeightPlusOption,
    },
    {
        optionType: 'MOLCO',
        nominatedValueType: OptionValueType.CpWeightPlusOption,
        minValueType: OptionValueType.CpWeightMinusOption,
        maxValueType: OptionValueType.CpWeightPlusOption,
    },
    {
        optionType: 'MOO',
        nominatedValueType: OptionValueType.CpWeightPlusOption,
        minValueType: OptionValueType.NA,
        maxValueType: OptionValueType.CpWeightPlusOption,
    },
    {
        optionType: 'MCO',
        nominatedValueType: OptionValueType.CpWeightPlusOption,
        minValueType: OptionValueType.NA,
        maxValueType: OptionValueType.CpWeightPlusOption,
    },
    {
        optionType: 'LOO',
        nominatedValueType: OptionValueType.CpWeight,
        minValueType: OptionValueType.CpWeightMinusOption,
        maxValueType: OptionValueType.NA,
    },
    {
        optionType: 'LCO',
        nominatedValueType: OptionValueType.CpWeight,
        minValueType: OptionValueType.CpWeightMinusOption,
        maxValueType: OptionValueType.NA,
    },
    {
        optionType: 'Min / Max',
        nominatedValueType: OptionValueType.CpWeight,
        minValueType: OptionValueType.CpWeight,
        maxValueType: OptionValueType.CpWeight,
    },
    { optionType: '', nominatedValueType: OptionValueType.CpWeight, minValueType: OptionValueType.NA, maxValueType: OptionValueType.NA },
];

export const getOptionConfiguration = (cargo: RotationCargo): OptionConfiguration | null => {
    return optionsConfiguration.find((option) => option.optionType === cargo.optionType) ?? null;
};

const getOptionAdjustement = (cargo: RotationCargo, optionValueType: OptionValueType): number | null => {
    switch (optionValueType) {
        case OptionValueType.NA:
            return null;
        case OptionValueType.CpWeight:
            return 1;
        case OptionValueType.CpWeightMinusOption:
            return 1 + cargo.optionMinusPercentage / 100;
        case OptionValueType.CpWeightPlusOption:
            return 1 + cargo.optionPlusPercentage / 100;
    }
};

export const getNominatedWeighAfterOptionAdjustement = (contractWeight: number, cargo: RotationCargo): number | null => {
    const optionConfiguration = getOptionConfiguration(cargo);
    if (!optionConfiguration) return null;
    const optionAdjustement = getOptionAdjustement(cargo, optionConfiguration.nominatedValueType);
    return optionAdjustement ? contractWeight * optionAdjustement : null;
};
export const getContractWeightFromOptionAdjustedNominatedWeight = (nominatedWeight: number, cargo: RotationCargo): number | null => {
    const optionConfiguration = getOptionConfiguration(cargo);
    if (!optionConfiguration) return null;
    const optionAdjustement = getOptionAdjustement(cargo, optionConfiguration.nominatedValueType);
    return optionAdjustement ? nominatedWeight / optionAdjustement : null;
};
export const getNominatedWeightMinimum = (contractWeight: number, cargo: RotationCargo): number | null => {
    const optionConfiguration = getOptionConfiguration(cargo);
    if (!optionConfiguration) return null;
    const optionAdjustement = getOptionAdjustement(cargo, optionConfiguration.minValueType);
    return optionAdjustement ? contractWeight * optionAdjustement : null;
};
export const getNominatedWeightMaximum = (contractWeight: number, cargo: RotationCargo): number | null => {
    const optionConfiguration = getOptionConfiguration(cargo);
    if (!optionConfiguration) return null;
    const optionAdjustement = getOptionAdjustement(cargo, optionConfiguration.maxValueType);
    return optionAdjustement ? contractWeight * optionAdjustement : null;
};
export interface SwingValues {
    minWeight: number | null;
    maxWeight: number | null;
}

export const getSwingValues = (contractWeight: number, cargo: RotationCargo): SwingValues => {
    return {
        minWeight: getNominatedWeightMinimum(contractWeight, cargo),
        maxWeight: getNominatedWeightMaximum(contractWeight, cargo),
    };
};

export const getOptionString = (cargo: RotationCargo): string => {
    return `${cargo.optionPlusPercentage}% ${cargo.optionType}`;
};

export const shouldShowOptionMinusValue = (cargo: RotationCargo): boolean => {
    const optionConfiguration = getOptionConfiguration(cargo);
    return optionConfiguration?.minValueType !== OptionValueType.NA;
};

export const shouldShowOptionPlusValue = (cargo: RotationCargo): boolean => {
    const optionConfiguration = getOptionConfiguration(cargo);
    return optionConfiguration?.maxValueType !== OptionValueType.NA;
};

type OperationTypeMap = {
    [key: string]: string;
};

const OPERATION_TYPE_MAP: OperationTypeMap = {
    Loading: OperationType.LOADING,
    Unloading: OperationType.UNLOADING,
    Transit: OperationType.TRANSIT,
    Transfer: OperationType.TRANSFER,
};

const IBC_SHIP_TYPE_MAP = {
    [IbcShipTypeEnum.Unknown]: '?',
    [IbcShipTypeEnum.One]: '1',
    [IbcShipTypeEnum.Two]: '2',
    [IbcShipTypeEnum.TwoK]: '2k',
    [IbcShipTypeEnum.Three]: '3',
};

const MARPOL_POLLUTION_MAP = {
    [MarpolPollutionCategoryEnum.Unknown]: '?',
    [MarpolPollutionCategoryEnum.X]: 'X',
    [MarpolPollutionCategoryEnum.Y]: 'Y',
    [MarpolPollutionCategoryEnum.Z]: 'Z',
    [MarpolPollutionCategoryEnum.OS]: 'OS',
};

const IBC_TANK_ENVIRONMENTAL_CONTROL_MAP = {
    [IbcTankEnvironmentalControlEnum.Unknown]: '?',
    [IbcTankEnvironmentalControlEnum.Inerting]: 'Inert',
    [IbcTankEnvironmentalControlEnum.LiquidOrGasPadding]: 'Pad',
    [IbcTankEnvironmentalControlEnum.Drying]: 'Dry',
    [IbcTankEnvironmentalControlEnum.NaturalOrForcedVentilation]: 'Vent',
    [IbcTankEnvironmentalControlEnum.NoSpecialRequirements]: 'No',
    [IbcTankEnvironmentalControlEnum.PaddingAndVentilationorInerting]: 'Pad+(vent or Inert)',
    [IbcTankEnvironmentalControlEnum.PaddingAndInerting]: 'Pad+Inert',
    [IbcTankEnvironmentalControlEnum.VentilationOrPadding]: 'Vent or Pad',
};

export const getOperationType = (opType: string) => OPERATION_TYPE_MAP[opType] || '';
export const getIbcShipTypeString = (shipType: IbcShipTypeEnum) => IBC_SHIP_TYPE_MAP[shipType] || '';
export const getMarpolPollutionCategoryString = (pollutionType: MarpolPollutionCategoryEnum) => MARPOL_POLLUTION_MAP[pollutionType] || '';
export const getIbcTankEnvironmentalControlString = (environmentalControl: IbcTankEnvironmentalControlEnum) =>
    IBC_TANK_ENVIRONMENTAL_CONTROL_MAP[environmentalControl] || '';
